import React, { useState, useEffect, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent } from '@/components/ui/card'
import { useExercisePlanStore } from '@/stores/exercisePlanStore'
import {
  Search,
  Filter,
  Plus,
  Play,
  Target,
  Clock,
  Dumbbell,
  X,
  Check,
  Image as ImageIcon
} from 'lucide-react'

const ExerciseLibraryModal = ({ isOpen, onClose, selectedPlan }) => {
  const [selectedSection, setSelectedSection] = useState(null)
  const [selectedExercises, setSelectedExercises] = useState([])
  const [showSectionSelector, setShowSectionSelector] = useState(false)

  const {
    exerciseLibrary,
    exerciseCategories,
    searchTerm,
    selectedFilters,
    setSearchTerm,
    setFilters,
    getFilteredExercises,
    searchExerciseLibrary,
    addExerciseToSection,
    loadExerciseLibrary
  } = useExercisePlanStore()

  // Load exercise library if not already loaded
  useEffect(() => {
    if (exerciseLibrary.length === 0) {
      loadExerciseLibrary()
    }
  }, [exerciseLibrary.length, loadExerciseLibrary])

  const filteredExercises = useMemo(() => {
    let exercises = exerciseLibrary

    // Apply search filter
    if (searchTerm.trim()) {
      exercises = searchExerciseLibrary(searchTerm)
    }

    // Apply category filter
    if (selectedFilters.category !== 'all') {
      exercises = exercises.filter(exercise => 
        exercise.category === selectedFilters.category ||
        exercise.muscleGroups?.some(group => group.includes(selectedFilters.category.toLowerCase()))
      )
    }

    // Apply equipment filter
    if (selectedFilters.equipment !== 'all') {
      exercises = exercises.filter(exercise => exercise.equipment === selectedFilters.equipment)
    }

    // Apply difficulty filter
    if (selectedFilters.difficulty !== 'all') {
      exercises = exercises.filter(exercise => exercise.difficulty === selectedFilters.difficulty)
    }

    // Apply muscle group filter
    if (selectedFilters.muscleGroup !== 'all') {
      exercises = exercises.filter(exercise => 
        exercise.muscleGroups?.includes(selectedFilters.muscleGroup)
      )
    }

    return exercises
  }, [exerciseLibrary, searchTerm, selectedFilters, searchExerciseLibrary])

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedExercises([])
      setShowSectionSelector(false)
      setSelectedSection(null)
    } else {
      setSearchTerm('')
      setFilters({
        category: 'all',
        equipment: 'all',
        difficulty: 'all',
        muscleGroup: 'all'
      })
    }
  }, [isOpen, setSearchTerm, setFilters])

  const categories = useMemo(() => [
    { id: 'all', name: 'All Categories' },
    ...exerciseCategories.map(category => ({
      id: category.toLowerCase(),
      name: category.charAt(0).toUpperCase() + category.slice(1)
    }))
  ], [exerciseCategories])

  const equipment = useMemo(() => {
    const equipmentSet = new Set()
    exerciseLibrary.forEach(exercise => {
      if (exercise.equipment) {
        equipmentSet.add(exercise.equipment)
      }
    })
    return [
      { id: 'all', name: 'All Equipment' },
      ...Array.from(equipmentSet).sort().map(eq => ({
        id: eq,
        name: eq.charAt(0).toUpperCase() + eq.slice(1)
      }))
    ]
  }, [exerciseLibrary])

  const difficulties = [
    { id: 'all', name: 'All Levels' },
    { id: 'beginner', name: 'Beginner' },
    { id: 'intermediate', name: 'Intermediate' },
    { id: 'advanced', name: 'Advanced' }
  ]

  const muscleGroups = [
    { id: 'all', name: 'All Muscles' },
    { id: 'chest', name: 'Chest' },
    { id: 'back', name: 'Back' },
    { id: 'shoulders', name: 'Shoulders' },
    { id: 'biceps', name: 'Biceps' },
    { id: 'triceps', name: 'Triceps' },
    { id: 'quadriceps', name: 'Quadriceps' },
    { id: 'hamstrings', name: 'Hamstrings' },
    { id: 'glutes', name: 'Glutes' },
    { id: 'calves', name: 'Calves' },
    { id: 'core', name: 'Core' }
  ]

  const handleExerciseSelect = (exercise) => {
    setSelectedExercises(prev => {
      const isSelected = prev.find(ex => ex.id === exercise.id)
      if (isSelected) {
        return prev.filter(ex => ex.id !== exercise.id)
      } else {
        return [...prev, exercise]
      }
    })
  }

  const handleAddToSection = () => {
    if (!selectedSection || selectedExercises.length === 0) return

    selectedExercises.forEach(exercise => {
      const exerciseData = {
        exerciseId: exercise.id,
        name: exercise.name,
        sets: 3,
        reps: '8-12',
        rest: 60,
        tempo: '2-1-2-1',
        notes: ''
      }
      addExerciseToSection(selectedPlan.id, selectedSection.id, exerciseData)
    })

    setSelectedExercises([])
    setShowSectionSelector(false)
    setSelectedSection(null)
    onClose()
  }

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'beginner': return 'text-green-600 bg-green-100'
      case 'intermediate': return 'text-yellow-600 bg-yellow-100'
      case 'advanced': return 'text-red-600 bg-red-100'
      default: return 'text-slate-600 bg-slate-100'
    }
  }

  if (!isOpen) return null

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-6xl h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Dumbbell className="h-5 w-5" />
            Exercise Library
            {selectedExercises.length > 0 && (
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-sm">
                {selectedExercises.length} selected
              </span>
            )}
          </DialogTitle>
        </DialogHeader>

        {/* Search and Filters */}
        <div className="border-b border-slate-200 pb-4">
          <div className="flex gap-4 mb-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search exercises..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <select
              value={selectedFilters.category}
              onChange={(e) => setFilters({ category: e.target.value })}
              className="px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>

            <select
              value={selectedFilters.equipment}
              onChange={(e) => setFilters({ equipment: e.target.value })}
              className="px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {equipment.map(eq => (
                <option key={eq.id} value={eq.id}>{eq.name}</option>
              ))}
            </select>

            <select
              value={selectedFilters.difficulty}
              onChange={(e) => setFilters({ difficulty: e.target.value })}
              className="px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {difficulties.map(diff => (
                <option key={diff.id} value={diff.id}>{diff.name}</option>
              ))}
            </select>

            <select
              value={selectedFilters.muscleGroup}
              onChange={(e) => setFilters({ muscleGroup: e.target.value })}
              className="px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {muscleGroups.map(muscle => (
                <option key={muscle.id} value={muscle.id}>{muscle.name}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Exercise Grid */}
        <div className="flex-1 overflow-y-auto">
          {filteredExercises.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <div className="text-center">
                <Target className="h-12 w-12 text-slate-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">No exercises found</h3>
                <p className="text-slate-600">Try adjusting your search or filters</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-4">
              {filteredExercises.map((exercise, index) => {
                const isSelected = selectedExercises.find(ex => ex.id === exercise.id)
                
                return (
                  <motion.div
                    key={exercise.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Card 
                      className={`cursor-pointer transition-all duration-200 ${
                        isSelected ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:bg-slate-50'
                      }`}
                      onClick={() => handleExerciseSelect(exercise)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start gap-3">
                          {/* Exercise Image */}
                          <div className="w-16 h-16 bg-slate-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            {exercise.image ? (
                              <img 
                                src={exercise.image} 
                                alt={exercise.name}
                                className="w-full h-full object-cover rounded-lg"
                              />
                            ) : (
                              <ImageIcon className="h-8 w-8 text-slate-400" />
                            )}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between mb-2">
                              <h3 className="font-semibold text-slate-900 truncate">
                                {exercise.name}
                              </h3>
                              {isSelected && (
                                <div className="bg-blue-500 text-white rounded-full p-1">
                                  <Check className="h-3 w-3" />
                                </div>
                              )}
                            </div>

                            <p className="text-sm text-slate-600 mb-3 line-clamp-2">
                              {exercise.description}
                            </p>

                            <div className="flex items-center justify-between mb-2">
                              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(exercise.difficulty)}`}>
                                {exercise.difficulty}
                              </span>
                              <span className="text-xs text-slate-500 capitalize">
                                {exercise.equipment}
                              </span>
                            </div>

                            <div className="flex items-center gap-2 text-xs text-slate-500">
                              <span className="capitalize">{exercise.category}</span>
                              {exercise.videoUrl && (
                                <div className="flex items-center">
                                  <Play className="h-3 w-3 mr-1" />
                                  <span>Video</span>
                                </div>
                              )}
                            </div>

                            {exercise.muscleGroups && exercise.muscleGroups.length > 0 && (
                              <div className="mt-2 flex flex-wrap gap-1">
                                {exercise.muscleGroups.slice(0, 3).map(muscle => (
                                  <span key={muscle} className="px-1.5 py-0.5 bg-slate-100 text-xs text-slate-600 rounded">
                                    {muscle}
                                  </span>
                                ))}
                                {exercise.muscleGroups.length > 3 && (
                                  <span className="px-1.5 py-0.5 bg-slate-100 text-xs text-slate-600 rounded">
                                    +{exercise.muscleGroups.length - 3}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>
                )
              })}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="border-t border-slate-200 pt-4 flex items-center justify-between">
          <div className="text-sm text-slate-600">
            {filteredExercises.length} exercise{filteredExercises.length !== 1 ? 's' : ''} available
          </div>
          
          <div className="flex gap-3">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            
            {selectedExercises.length > 0 && (
              <Button
                variant="fitness"
                onClick={() => setShowSectionSelector(true)}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add {selectedExercises.length} Exercise{selectedExercises.length !== 1 ? 's' : ''}
              </Button>
            )}
          </div>
        </div>

        {/* Section Selector Modal */}
        <AnimatePresence>
          {showSectionSelector && selectedPlan && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
              onClick={() => setShowSectionSelector(false)}
            >
              <motion.div
                initial={{ scale: 0.9, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.9, opacity: 0 }}
                className="bg-white rounded-lg p-6 max-w-md w-full mx-4"
                onClick={(e) => e.stopPropagation()}
              >
                <h3 className="text-lg font-semibold text-slate-900 mb-4">
                  Select Section
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  Choose which section to add the selected exercises to:
                </p>
                
                <div className="space-y-2 mb-6">
                  {selectedPlan.sections.map(section => (
                    <button
                      key={section.id}
                      onClick={() => setSelectedSection(section)}
                      className={`w-full p-3 text-left rounded-lg border transition-colors ${
                        selectedSection?.id === section.id
                          ? 'border-blue-500 bg-blue-50 text-blue-900'
                          : 'border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <div className="font-medium">{section.name}</div>
                      <div className="text-sm text-slate-600">
                        {section.exercises.length} exercise{section.exercises.length !== 1 ? 's' : ''}
                      </div>
                    </button>
                  ))}
                </div>
                
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowSectionSelector(false)}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="fitness"
                    onClick={handleAddToSection}
                    disabled={!selectedSection}
                    className="flex-1"
                  >
                    Add to Section
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  )
}

export default ExerciseLibraryModal